import { StatusLabel } from "@/Components/v2/atoms/StatusLabel";
import { formatPercentValue } from "@/Utils/formatters";
import {
  createDeleteOption,
  createShareLinkOption,
  TableContextMenu,
} from "@/pages/scenario-builder/v3/components/TableContextMenu";
import { formatDateLocale } from "@/shared/utils/formatDate";
import { GridColDef } from "@mui/x-data-grid-premium";
import { ActionPlanStatus } from "@mutiny-group/sbo-openapi-client";
import dayjs from "dayjs";
import { ActionPlanStatusMapping } from "../types";

type GetColumnDefinitionsOptions = {
  onDelete: (actionPlanUuid: string, actionPlanName: string) => void;
  isGeoEnabled: boolean;
};

export const getColumnDefinitions = ({ onDelete, isGeoEnabled }: GetColumnDefinitionsOptions) => {
  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      minWidth: 200,
    },
    {
      field: "scopeBrandForDisplay",
      headerName: "Business Unit",
      flex: 1,
    },
    ...(isGeoEnabled
      ? [
          {
            field: "scopeGeographies",
            headerName: "Geography",
            flex: 1,
            valueFormatter: ({ value }: { value: string[] | undefined }) => {
              if (!value || value.length === 0) {
                return "All Geographies";
              }
              return value.join(", ");
            },
          },
        ]
      : []),
    {
      field: "createdAt",
      headerName: "Created On",
      flex: 1,
      valueGetter: ({ row }) => dayjs(row.createdAt),
      valueFormatter: ({ value }) => formatDateLocale(dayjs(value), { dateFormat: "DD MMM YY" }),
    },
    {
      field: "endDate",
      headerName: "End Date",
      flex: 1,
      valueGetter: ({ row }) => dayjs(row.endDate),
      valueFormatter: ({ value }) => formatDateLocale(dayjs(value), { dateFormat: "DD MMM YY" }),
    },
    {
      field: "revenuePercentage",
      headerName: "Revenue %",
      flex: 1,
      valueFormatter: ({ value }) => formatPercentValue(value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: ({ value }) => {
        const status = value as ActionPlanStatus;
        const { label, style } = ActionPlanStatusMapping[status];
        const statusLabelStyle = {
          icon: status !== ActionPlanStatus.Complete ? undefined : style.icon,
          color: style.color,
          textColor: status === ActionPlanStatus.Complete ? undefined : style.color,
        };

        return <StatusLabel label={label} style={statusLabelStyle} />;
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.5,
      minWidth: 60,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <TableContextMenu
            options={[
              createShareLinkOption(
                `/scenarios-v3/action-plans/${row.uuid}`,
                "Action Plan URL copied to clipboard"
              ),
              createDeleteOption(() => onDelete(row.uuid, row.name)),
            ]}
          />
        );
      },
    },
  ];

  return columns;
};
